// Gestione.jsx
import React, { useState } from "react";
import dayjs from "dayjs";
import Select from "react-select";
import { deleteAllOrdersByDayAndMonth, deleteAllCostsByDayAndMonth } from "../../utility/EndPointGoogleFirebase";
import Notification from "../../utility/Notification";
import { FaTrashAlt, FaExclamationTriangle } from "react-icons/fa";

const months = [
  { value: 1,  label: "Gennaio" },
  { value: 2,  label: "Febbraio" },
  { value: 3,  label: "Marzo" },
  { value: 4,  label: "Aprile" },
  { value: 5,  label: "Maggio" },
  { value: 6,  label: "Giugno" },
  { value: 7,  label: "Luglio" },
  { value: 8,  label: "Agosto" },
  { value: 9,  label: "Settembre" },
  { value: 10, label: "Ottobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Dicembre" },
];

const years = Array.from({ length: 20 }, (_, i) => {
  const year = dayjs().year() - i;
  return { value: year, label: year.toString() };
});

// ── Confirm Modal ────────────────────────────────────────────────────
const ConfirmModal = ({ step, action, month, year, onCancel, onFirstConfirm, onFinalConfirm, isLoading }) => {
  if (!step) return null;
  const actionLabel = action === "orders" ? "Ordini" : "Spese";
  const accentColor = action === "orders" ? "#dc2626" : "#d97706";

  return (
    <div
      onClick={step === 1 ? onCancel : undefined}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        background: "rgba(17,24,39,0.5)",
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16,
          border: "1px solid #e5e7eb",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          padding: "28px 24px", width: "100%", maxWidth: 380,
        }}
      >
        <div style={{
          width: 52, height: 52, borderRadius: "50%",
          background: "#fff7ed", border: "1px solid #fed7aa",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 16px",
        }}>
          <FaExclamationTriangle style={{ color: accentColor, fontSize: 22 }} />
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: "#111827", margin: "0 0 10px", textAlign: "center" }}>
          {step === 1 ? "Conferma eliminazione" : "Ultima conferma"}
        </h3>
        <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7, margin: "0 0 22px", textAlign: "center" }}>
          {step === 1
            ? <>Vuoi eliminare tutti i dati di <strong style={{ color: "#111827" }}>{actionLabel}</strong> per <strong style={{ color: "#111827" }}>{month} {year}</strong>?<br /><span style={{ color: "#dc2626", fontSize: 12 }}>Questa operazione è irreversibile.</span></>
            : <>Stai per eliminare definitivamente tutti i dati di <strong style={{ color: "#111827" }}>{actionLabel}</strong> per <strong style={{ color: "#111827" }}>{month} {year}</strong>. Sei sicuro?</>
          }
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: "10px", borderRadius: 8,
              border: "1px solid #e5e7eb", background: "#f9fafb",
              color: "#374151", fontSize: 14, fontWeight: 500,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            Annulla
          </button>
          <button
            onClick={step === 1 ? onFirstConfirm : onFinalConfirm}
            disabled={isLoading}
            style={{
              flex: 1, padding: "10px", borderRadius: 8, border: "none",
              background: isLoading ? "#e5e7eb" : accentColor,
              color: isLoading ? "#9ca3af" : "#fff",
              fontSize: 14, fontWeight: 500,
              cursor: isLoading ? "not-allowed" : "pointer",
              fontFamily: "inherit", transition: "background 0.15s",
            }}
          >
            {isLoading ? "Eliminazione..." : step === 1 ? "Continua" : "Elimina"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Action card ──────────────────────────────────────────────────────
const ActionCard = ({ label, description, accentColor, icon, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: "#fff", borderRadius: 14,
      border: "1px solid #e5e7eb",
      borderTop: `3px solid ${accentColor}`,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      padding: "24px 20px",
      cursor: "pointer",
      display: "flex", flexDirection: "column", alignItems: "center",
      gap: 12, textAlign: "center",
      transition: "box-shadow 0.2s, transform 0.15s",
      userSelect: "none", WebkitTapHighlightColor: "transparent",
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "translateY(0)"; }}
  >
    <div style={{
      width: 48, height: 48, borderRadius: "50%",
      background: `${accentColor}15`, border: `1.5px solid ${accentColor}44`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: accentColor, fontSize: 20,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, color: "#111827", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 12, color: "#9ca3af" }}>{description}</div>
    </div>
  </div>
);

// ── Main component ───────────────────────────────────────────────────
const Gestione = ({ mesiDaPulire = 0 }) => {
  const [selectedMonth, setSelectedMonth] = useState(months[dayjs().month()]);
  const [selectedYear, setSelectedYear]   = useState(years[0]);
  const [modalStep, setModalStep]         = useState(0);
  const [modalAction, setModalAction]     = useState(null);
  const [isLoading, setIsLoading]         = useState(false);
  const [notification, setNotification]   = useState(null);

  const openModal = (action) => { setModalAction(action); setModalStep(1); };
  const closeModal = () => { setModalStep(0); setModalAction(null); };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      if (modalAction === "orders") {
        await deleteAllOrdersByDayAndMonth(selectedMonth.value, selectedYear.value);
        setNotification({ type: "success", message: `Ordini di ${selectedMonth.label} ${selectedYear.value} cancellati.` });
      } else {
        await deleteAllCostsByDayAndMonth(selectedMonth.value, selectedYear.value);
        setNotification({ type: "success", message: `Spese di ${selectedMonth.label} ${selectedYear.value} cancellate.` });
      }
    } catch (error) {
      setNotification({ type: "error", message: "Errore durante l'eliminazione." });
    } finally {
      setIsLoading(false);
      closeModal();
    }
  };

  const selectStyles = {
    control: (base) => ({
      ...base, borderRadius: 8, borderColor: "#e5e7eb",
      boxShadow: "none", fontSize: 14,
      "&:hover": { borderColor: "#a5b4fc" },
    }),
    option: (base, { isFocused }) => ({
      ...base, fontSize: 14,
      background: isFocused ? "#eef2ff" : "#fff",
      color: "#111827",
    }),
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        .gt-loader-ring {
          width: 36px; height: 36px;
          border: 2px solid #e5e7eb; border-top-color: #4f46e5;
          border-radius: 50%; animation: gtSpin 0.8s linear infinite;
        }
        @keyframes gtSpin { to { transform: rotate(360deg); } }
        .gt-wrapper {
          max-width: 700px; margin: 0 auto; padding: 16px 12px;
          padding-left: max(12px, env(safe-area-inset-left));
          padding-right: max(12px, env(safe-area-inset-right));
          padding-bottom: max(32px, env(safe-area-inset-bottom));
          display: flex; flex-direction: column; gap: 16px;
        }
        @media (min-width: 480px)  { .gt-wrapper { padding: 20px 16px; } }
        @media (min-width: 768px)  { .gt-wrapper { padding: 28px 24px; } }
        @media (min-width: 1024px) { .gt-wrapper { padding: 36px 32px; } }
        .page-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(22px, 5vw, 34px);
          font-weight: 400; color: #111827; line-height: 1.2;
        }
        .gt-actions {
          display: grid; grid-template-columns: 1fr; gap: 14px;
        }
        @media (min-width: 480px) { .gt-actions { grid-template-columns: repeat(2, 1fr); } }
      `}</style>

      {isLoading && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 60,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14,
          background: "rgba(249,250,251,0.88)",
          backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        }}>
          <div className="gt-loader-ring" />
          <p style={{ fontSize: 12, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Eliminazione in corso
          </p>
        </div>
      )}

      <Notification notification={notification} onClose={() => setNotification(null)} />

      <ConfirmModal
        step={modalStep}
        action={modalAction}
        month={selectedMonth?.label}
        year={selectedYear?.value}
        onCancel={closeModal}
        onFirstConfirm={() => setModalStep(2)}
        onFinalConfirm={handleDelete}
        isLoading={isLoading}
      />

      <div className="gt-wrapper">

        {/* Page header */}
        <div style={{ textAlign: "center", paddingBottom: 20, borderBottom: "1px solid #e5e7eb" }}>
          <p style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#9ca3af", marginBottom: 6 }}>
            Amministrazione
          </p>
          <h1 className="page-title">Gestione Database</h1>
          <div style={{ width: 28, height: 2, background: "#4f46e5", borderRadius: 2, margin: "10px auto 0" }} />
        </div>

        {/* ── Banner avviso mesi vecchi ── */}
        {mesiDaPulire > 0 && (
          <div style={{
            background: "#fff7ed",
            border: "0.5px solid #fed7aa",
            borderLeft: "3px solid #d97706",
            borderRadius: "0 10px 10px 0",
            padding: "12px 16px",
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <FaExclamationTriangle style={{ color: "#d97706", fontSize: 16, flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color: "#92400e", margin: "0 0 2px" }}>
                Hai {mesiDaPulire} {mesiDaPulire === 1 ? "mese" : "mesi"} da pulire
              </p>
              <p style={{ fontSize: 12, color: "#b45309", margin: 0 }}>
                Seleziona il periodo qui sotto e cancella i dati vecchi.
              </p>
            </div>
          </div>
        )}

        {/* Selettori mese/anno */}
        <div style={{
          background: "#fff", borderRadius: 14,
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          padding: "20px 16px",
        }}>
          <p style={{ fontSize: 11, fontWeight: 500, color: "#9ca3af", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 14 }}>
            Periodo di riferimento
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: "#374151", display: "block", marginBottom: 6 }}>
                Mese
              </label>
              <Select options={months} value={selectedMonth} onChange={setSelectedMonth} styles={selectStyles} />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: "#374151", display: "block", marginBottom: 6 }}>
                Anno
              </label>
              <Select options={years} value={selectedYear} onChange={setSelectedYear} styles={selectStyles} />
            </div>
          </div>
        </div>

        {/* Action cards */}
        <div className="gt-actions">
          <ActionCard
            label="Cancella Ordini"
            description="Elimina tutti gli ordini per il periodo selezionato"
            accentColor="#dc2626"
            icon={<FaTrashAlt />}
            onClick={() => openModal("orders")}
          />
          <ActionCard
            label="Cancella Spese"
            description="Elimina tutte le spese per il periodo selezionato"
            accentColor="#d97706"
            icon={<FaTrashAlt />}
            onClick={() => openModal("costs")}
          />
        </div>

      </div>
    </div>
  );
};

export default Gestione;